import { useState } from "react";
import { Button, Upload, Typography } from "antd";
import { UploadOutlined, CheckCircleOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";

const { Text } = Typography;

const uploadButtonStyle = (active: boolean): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  maxWidth: 240,
  ...(active
    ? {
        borderColor: "#52c41a",
        color: "#52c41a",
      }
    : {}),
});

const buttonLabel = (
  files: { name: string }[],
  multiple: boolean,
  fallback: string
) => {
  if (files.length === 0) return fallback;
  if (files.length === 1) return files[0].name;
  return `${files.length} файл${multiple ? "" : ""} сонгогдсон`;
};

interface SimpleUploadSectionProps {
  label: string;
  files: UploadFile[];
  onUpload: (files: UploadFile[]) => void;
  statusText?: React.ReactNode;
  multiple?: boolean;
  subtitle?: string;
  extraButtons?: React.ReactNode;
  accept?: string;
}

export const SimpleUploadSection = ({
  label,
  files,
  onUpload,
  multiple = false,
  subtitle,
  accept = ".xlsx,.xls",
}: SimpleUploadSectionProps) => (
  <div>
    <div
      style={{
        fontSize: 13,
        fontWeight: 500,
        color: "#262626",
        marginBottom: subtitle ? 4 : 8,
      }}
    >
      {label}
    </div>
    {subtitle && (
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{subtitle}</Text>
      </div>
    )}
    <Upload
      multiple={multiple}
      accept={accept}
      fileList={files}
      showUploadList={false}
      beforeUpload={() => false}
      onChange={({ fileList }) => onUpload(fileList)}
    >
      <Button
        icon={
          files.length > 0 ? <CheckCircleOutlined /> : <UploadOutlined />
        }
        style={uploadButtonStyle(files.length > 0)}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {buttonLabel(files, multiple, `Файл ${multiple ? "нэмэх" : "сонгох"}`)}
        </span>
      </Button>
    </Upload>
  </div>
);

interface AccumulativeUploadSectionProps {
  label: string;
  sheets: { name: string; data: unknown[] }[];
  onFoundation: (file: File) => void;
  onAddSheet?: (file: File) => void;
  onDownload: () => void;
  onClear: () => void;
}

export const AccumulativeUploadSection = ({
  label,
  sheets,
  onFoundation,
}: AccumulativeUploadSectionProps) => {
  const [loading, setLoading] = useState(false);

  const handleUpload = async (file: File) => {
    setLoading(true);
    try {
      await onFoundation(file);
    } finally {
      setLoading(false);
    }
  };

  const hasFiles = sheets.length > 0;
  const firstSheetName = sheets[0]?.name || "";

  return (
    <div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: "#262626",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <Upload
        accept=".xlsx,.xls"
        showUploadList={false}
        beforeUpload={(file) => {
          handleUpload(file);
          return false;
        }}
      >
        <Button
          icon={hasFiles ? <CheckCircleOutlined /> : <UploadOutlined />}
          loading={loading}
          style={uploadButtonStyle(hasFiles)}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {loading
              ? "Уншиж байна..."
              : hasFiles
              ? sheets.length === 1
                ? firstSheetName
                : `${sheets.length} файл сонгогдсон`
              : "Файл сонгох"}
          </span>
        </Button>
      </Upload>
    </div>
  );
};
